import React, { useState, useEffect, useCallback, useRef, useMemo, ReactNode } from 'react';
import styles from './Carousel.module.scss';

export type CarouselSlide = {
  /** Image URL for the slide */
  url: string;
  /** Alt text for accessibility */
  alt: string;
};

export type CarouselProps = {
  /** Array of slides to display */
  slides: CarouselSlide[];
  /** Theme variant */
  theme?: 'light' | 'dark';
  /** Auto-slide interval in milliseconds. Set to 0 to disable auto-play */
  autoPlayInterval?: number;
  /** Height of the carousel */
  height?: number | string;
  /** Show slide indicators */
  showIndicators?: boolean;
  /** Content to overlay on the carousel */
  children?: ReactNode;
  /** Additional class name */
  className?: string;
  /** Test ID for testing */
  dataTestId?: string;
};

/**
 * @component Carousel
 * @description A reusable image carousel with infinite loop, auto-play, and overlay support
 * @param {CarouselProps} props - Component props
 * @returns {JSX.Element} Carousel component
 * @example
 * <Carousel
 *   slides={[{ url: 'image1.jpg', alt: 'Image 1' }]}
 *   autoPlayInterval={3000}
 *   height={500}
 * >
 *   <Typography variant="display" size="small">Overlay Text</Typography>
 * </Carousel>
 */
const Carousel: React.FC<CarouselProps> = ({
  slides,
  theme = 'dark',
  autoPlayInterval = 3000,
  height = 500,
  showIndicators = true,
  children,
  className,
  dataTestId = 'carousel',
}) => {
  // Create extended array with clones for infinite loop effect
  const extendedSlides = useMemo(() => {
    if (slides.length === 0) return [];
    return [
      slides[slides.length - 1], // Clone of last slide at beginning
      ...slides,
      slides[0], // Clone of first slide at end
    ];
  }, [slides]);

  // Start at index 1 (first real slide, after the cloned last slide)
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlayInterval > 0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);

  // Get the actual slide index (0-based, for indicators)
  const actualIndex = currentIndex - 1;

  const goToSlide = useCallback((index: number) => {
    setIsTransitioning(true);
    // Add 1 because index 0 in extended array is the cloned last slide
    setCurrentIndex(index + 1);
  }, []);

  const goToNextSlide = useCallback(() => {
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => prevIndex + 1);
  }, []);

  // Handle infinite loop - jump without animation when reaching clones
  const handleTransitionEnd = useCallback(() => {
    // If we're at the cloned first slide (end), jump to real first slide
    if (currentIndex === extendedSlides.length - 1) {
      setIsTransitioning(false);
      setCurrentIndex(1);
    }
    // If we're at the cloned last slide (beginning), jump to real last slide
    else if (currentIndex === 0) {
      setIsTransitioning(false);
      setCurrentIndex(slides.length);
    }
  }, [currentIndex, extendedSlides.length, slides.length]);

  // Pause auto-play when page is not visible (tab switched, minimized, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsAutoPlaying(false);
      } else if (autoPlayInterval > 0) {
        setIsAutoPlaying(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [autoPlayInterval]);

  useEffect(() => {
    if (!isAutoPlaying || autoPlayInterval <= 0) return;

    const intervalId = setInterval(() => {
      goToNextSlide();
    }, autoPlayInterval);

    return () => clearInterval(intervalId);
  }, [isAutoPlaying, autoPlayInterval, goToNextSlide]);

  const handleIndicatorClick = (index: number) => {
    setIsAutoPlaying(false);
    goToSlide(index);
    // Resume auto-play after 10 seconds of inactivity
    if (autoPlayInterval > 0) {
      setTimeout(() => setIsAutoPlaying(true), 10000);
    }
  };

  if (slides.length === 0) {
    return null;
  }

  const cssVars = {
    '--carousel-height': typeof height === 'number' ? `${height}px` : height,
    '--carousel-translate': `-${currentIndex * 100}%`,
    '--carousel-transition': isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
  } as React.CSSProperties;

  return (
    <div
      className={`${styles.carouselWrapper} ${className || ''}`}
      data-testid={dataTestId}
      data-theme={theme}
      style={cssVars} // eslint-disable-line react/forbid-dom-props -- CSS variables required for dynamic height and transitions
    >
      <div className={styles.carouselContainer}>
        <div ref={trackRef} className={styles.slidesTrack} onTransitionEnd={handleTransitionEnd}>
          {extendedSlides.map((slide, index) => (
            <div key={index} className={styles.slide}>
              <img src={slide.url} alt={slide.alt} className={styles.slideImage} />
            </div>
          ))}
        </div>

        {/* Overlay content */}
        {children && <div className={styles.overlay}>{children}</div>}

        {/* Slide Indicators */}
        {showIndicators && (
          <div className={styles.indicators}>
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                className={`${styles.indicator} ${index === actualIndex ? styles.active : ''}`}
                onClick={() => handleIndicatorClick(index)}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`${dataTestId}-indicator-${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

Carousel.displayName = 'Carousel';

export default Carousel;
